// Governed SHERLOC Risks / Dependencies findings (sherloc_risk_findings_v1).
//
// This module is byte-identical in the superadmin (Quantum) and client
// (Cortex) repos — keep every change synchronized.
//
// Contract: a risk or dependency may be shown ONLY when there is positive
// evidence of a real unresolved condition in the authoritative campaign
// state. Selecting a platform is never, by itself, a risk. Standard setup
// steps (trafficking, delivery checks, ordinary CPM math) are execution
// planning, not risks. Absence of evidence is never converted into a
// confident claim — when a dependency cannot be verified from the supplied
// evidence, this builder stays silent about it rather than inventing a
// finding. When nothing material is found the builder returns an empty list
// and the section is omitted entirely (the governed alternative to filler).
//
// The builder is a pure function of current evidence, so refinements
// recompute findings from the revised plan automatically: resolved findings
// disappear, and no stale boilerplate can survive from a previous strategy.

const CANONICAL_PLATFORM_LABELS = Object.freeze({
  meta: "Meta (Facebook + Instagram)",
  facebook: "Facebook",
  instagram: "Instagram",
  tiktok: "TikTok",
  x: "X",
  youtube: "YouTube",
  google_search: "Google Search",
  rumble: "Rumble",
  truth_social: "Truth Social",
  amazon: "Amazon",
  ctv: "CTV",
  video: "Online Video (OLV)",
  display: "Display",
  mobile: "Mobile",
  audio: "Audio",
  dooh: "Digital Out-of-Home (DOOH)",
  shopify: "Shopify",
  linkedin: "LinkedIn",
  ingame: "In-Game",
});

// Non-media layers never require a paid-media allocation row, so their
// absence from Budget Allocation is not an allocation gap.
const NON_MEDIA_PLATFORM_KEYS = Object.freeze(["shopify"]);

const compactKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const CANONICAL_KEY_BY_COMPACT = (() => {
  const map = {};
  Object.keys(CANONICAL_PLATFORM_LABELS).forEach((key) => {
    map[compactKey(key)] = key;
  });
  Object.assign(map, {
    metafacebookinstagram: "meta",
    facebookinstagram: "meta",
    fbig: "meta",
    twitter: "x",
    googleads: "google_search",
    google: "google_search",
    search: "google_search",
    paidsearch: "google_search",
    youtubeads: "youtube",
    connectedtv: "ctv",
    connectedtelevision: "ctv",
    onlinevideo: "video",
    programmaticvideo: "video",
    programmaticvideoolv: "video",
    programmaticolv: "video",
    olv: "video",
    olvvideo: "video",
    videoolv: "video",
    displayretargeting: "display",
    retargeting: "display",
    mobileads: "mobile",
    mobiledisplay: "mobile",
    inappmobile: "mobile",
    mobileinapp: "mobile",
    digitaloutofhome: "dooh",
    ooh: "dooh",
    truthsocial: "truth_social",
    amazonads: "amazon",
    amazonadvertising: "amazon",
    shopifycommerce: "shopify",
    shopifystore: "shopify",
    linkedinads: "linkedin",
    linkedinadvertising: "linkedin",
    ingames: "ingame",
  });
  return Object.freeze(map);
})();

export const normalizeSherlocRiskPlatformKey = (value) => {
  const compact = compactKey(
    value && typeof value === "object"
      ? value.key || value.platformKey || value.value || value.label || value.name
      : value
  );
  if (!compact) return "";
  return CANONICAL_KEY_BY_COMPACT[compact] || compact;
};

export const sherlocRiskPlatformLabel = (key) =>
  CANONICAL_PLATFORM_LABELS[key] || String(key || "").trim();

// Canonical selected set: facebook + instagram collapse into meta exactly the
// way the planner's own canonicalization does.
const canonicalSelectedKeys = (selectedPlatforms) => {
  const keys = new Set(
    (Array.isArray(selectedPlatforms) ? selectedPlatforms : [])
      .map(normalizeSherlocRiskPlatformKey)
      .filter(Boolean)
  );
  if (keys.has("facebook") || keys.has("instagram")) {
    if (keys.has("facebook") && keys.has("instagram")) {
      keys.delete("facebook");
      keys.delete("instagram");
    }
    keys.add("meta");
  }
  return Array.from(keys);
};

const canonicalKeySet = (rows) =>
  new Set(
    (Array.isArray(rows) ? rows : [])
      .map(normalizeSherlocRiskPlatformKey)
      .filter(Boolean)
  );

const labelList = (keys) => keys.map(sherlocRiskPlatformLabel).join(", ");

// coveredBy: a selected canonical key counts as covered when the covering set
// carries it directly, or carries the meta family equivalents.
const isCovered = (key, coveredSet) => {
  if (coveredSet.has(key)) return true;
  if (key === "meta") return coveredSet.has("facebook") && coveredSet.has("instagram");
  if (key === "facebook" || key === "instagram") return coveredSet.has("meta");
  return false;
};

/**
 * Build the governed Risks / Dependencies findings from authoritative
 * campaign evidence. Every finding identifies the actual issue, why it
 * matters, the affected area, and the required action.
 *
 * evidence = {
 *   selectedPlatforms: raw selected platform keys/labels,
 *   allocationRows:    the authoritative allocation rows (canonical
 *                      allocation / budget_breakdown) or null when no
 *                      allocation authority exists yet,
 *   presentationChannelKeys: canonical channel keys present in the governed
 *                      strategy presentation, or null when no presentation,
 *   allocationApprovalStatus: "" | "PENDING_APPROVAL" | "APPROVED" | "REJECTED",
 *   creativeReadiness: null when unverified (stay silent), otherwise
 *                      { requiredForLaunch: boolean, approvedCreativeCount: number },
 *   viewerRole:        "internal" | "client" — selects action wording only.
 * }
 */
export const buildSherlocRiskDependencyFindings = (evidence = {}) => {
  const {
    selectedPlatforms = [],
    allocationRows = null,
    presentationChannelKeys = null,
    allocationApprovalStatus = "",
    creativeReadiness = null,
    viewerRole = "internal",
  } = evidence || {};

  const findings = [];
  const selected = canonicalSelectedKeys(selectedPlatforms).filter(
    (key) => !NON_MEDIA_PLATFORM_KEYS.includes(key)
  );

  // 1) Allocation gap — only judged against an allocation authority that
  //    actually exists. No allocation yet is an approval/authority state,
  //    not a per-platform coverage risk.
  if (Array.isArray(allocationRows) && allocationRows.length > 0) {
    const allocated = canonicalKeySet(allocationRows);
    const missing = selected.filter((key) => !isCovered(key, allocated)).sort();
    if (missing.length) {
      findings.push({
        key: "platform-allocation-gap",
        title: "Platform allocation gap",
        issue: `${labelList(missing)} ${
          missing.length === 1 ? "is" : "are"
        } selected for this campaign but absent from the governed budget allocation.`,
        impact:
          "Spend for an unallocated platform cannot be planned, reconciled, or delivered — the plan's financial coverage is incomplete.",
        requiredAction:
          viewerRole === "client"
            ? "Your Cphere team will allocate budget to every selected platform (or remove it from scope) before launch."
            : `Add governed allocations for ${labelList(missing)} or remove ${
                missing.length === 1 ? "it" : "them"
              } from the selected platform set, then route the revised allocation through approval.`,
        affectedArea: `Financials — ${labelList(missing)}`,
      });
    }
  }

  // 2) Channel strategy gap — only judged against a presentation that exists.
  if (Array.isArray(presentationChannelKeys) && presentationChannelKeys.length > 0) {
    const covered = canonicalKeySet(presentationChannelKeys);
    const missing = selected.filter((key) => !isCovered(key, covered)).sort();
    if (missing.length) {
      findings.push({
        key: "channel-strategy-gap",
        title: "Channel strategy coverage gap",
        issue: `${labelList(missing)} ${
          missing.length === 1 ? "is" : "are"
        } selected for this campaign but ${
          missing.length === 1 ? "has" : "have"
        } no channel strategy in the governed presentation.`,
        impact:
          "A selected platform without a channel strategy cannot be briefed, executed, or measured against the plan.",
        requiredAction:
          viewerRole === "client"
            ? "Your Cphere team will complete the channel strategy for every selected platform before launch."
            : `Regenerate or refine the strategy so ${labelList(missing)} ${
                missing.length === 1 ? "receives" : "receive"
              } a governed channel strategy, or remove ${
                missing.length === 1 ? "it" : "them"
              } from the selected platform set.`,
        affectedArea: `Execution — ${labelList(missing)}`,
      });
    }
  }

  // 3) Allocation approval dependency — an accurate, unresolved governance
  //    state, not a reminder.
  if (String(allocationApprovalStatus).trim().toUpperCase() === "PENDING_APPROVAL") {
    findings.push({
      key: "allocation-approval-pending",
      title: "Channel allocation awaiting approval",
      issue:
        "A recommended channel allocation exists for this campaign but has not been approved.",
      impact:
        "Until the allocation is decided, the plan has no final financial authority and dependent surfaces stay blocked.",
      requiredAction:
        viewerRole === "client"
          ? "The allocation is being finalized; the strategy preview unlocks once it is approved."
          : "Review the recommended allocation in the SHERLOC Output and approve, edit, or reject it.",
      affectedArea: "Governance — budget allocation",
    });
  }

  // 4) Creative readiness — ONLY from explicit readiness evidence. A null /
  //    absent readiness object means verification is unavailable, and no
  //    claim is made.
  if (
    creativeReadiness &&
    typeof creativeReadiness === "object" &&
    creativeReadiness.requiredForLaunch === true &&
    Number(creativeReadiness.approvedCreativeCount) === 0
  ) {
    findings.push({
      key: "creative-readiness",
      title: "Creative assets not ready",
      issue:
        "No approved creative assets exist for this campaign, and creative is required before launch.",
      impact:
        "Without approved creatives the campaign cannot be trafficked or launched on any platform.",
      requiredAction:
        viewerRole === "client"
          ? "Creative production and approval must complete before the flight can start."
          : "Produce and approve launch creatives (or record the creative waiver) before flight start.",
      affectedArea: "Creative",
    });
  }

  return findings;
};
